import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, formatApiError, formatRelative } from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpen, RefreshCw, Loader2, CheckCircle2, XCircle, Clock,
  AlertTriangle, Ban, PlayCircle, History, ChevronRight, Download, Package,
  FileText, FileCode, FileJson, FileType2, FileArchive,
} from "lucide-react";
import {
  GovernanceDashboard, DocumentationTimeline, CompareVersions,
  DocumentationSearch, IntegrityVerification,
} from "./ArchitectureDocsVersionCenter";

/**
 * HarborWell Connect — Architecture & Developer Handoff
 *
 * Phase 1: module scaffold. Renders:
 *   - the 18-section navigation from GET /architecture-docs/catalog
 *   - version list + version detail from GET /architecture-docs/versions
 *   - background job launcher + poller
 *   - audit trail
 *
 * Discovery, generation, exports, comparison, restore, and visualization
 * arrive in Phases 2 – 6. The empty sections render Phase 1 placeholder
 * markdown returned by the backend.
 */

const STATUS_TONE = {
  queued:    { bg: "bg-[#E0E7FF]", text: "text-[#1E40AF]", Icon: Clock,        label: "Queued" },
  running:   { bg: "bg-[#DBEAFE]", text: "text-[#1E3A8A]", Icon: Loader2,      label: "Running", spin: true },
  succeeded: { bg: "bg-[#DCFCE7]", text: "text-[#166534]", Icon: CheckCircle2, label: "Succeeded" },
  failed:    { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", Icon: XCircle,      label: "Failed" },
  cancelled: { bg: "bg-[#F1F5F9]", text: "text-[#334155]", Icon: Ban,          label: "Cancelled" },
};

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.queued;
  const Icon = tone.Icon;
  return (
    <span
      data-testid={`arch-docs-status-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${tone.bg} ${tone.text}`}
    >
      <Icon className={`w-3.5 h-3.5 ${tone.spin ? "animate-spin" : ""}`} />
      {tone.label}
    </span>
  );
}

// Canonical ordering + presentation for downloadable export formats.
// Keys mirror the backend `exports_service.FORMATS_STAGE_2` set + the
// three metadata artifacts (manifest/coverage/validation) + the
// phase-6 diagrams placeholder.
const EXPORT_FORMAT_META = {
  markdown:              { label: "Markdown",         ext: ".md",   Icon: FileText,    tone: "text-[#1E3A8A] bg-[#EFF6FF]", downloadable: true },
  html:                  { label: "HTML",             ext: ".html", Icon: FileCode,    tone: "text-[#7C3AED] bg-[#F5F3FF]", downloadable: true },
  json:                  { label: "JSON",             ext: ".json", Icon: FileJson,    tone: "text-[#0F766E] bg-[#ECFDF5]", downloadable: true },
  docx:                  { label: "Microsoft Word",   ext: ".docx", Icon: FileType2,   tone: "text-[#2563EB] bg-[#DBEAFE]", downloadable: true, badge: "python-docx" },
  pdf:                   { label: "PDF",              ext: ".pdf",  Icon: FileText,    tone: "text-[#B91C1C] bg-[#FEE2E2]", downloadable: true, badge: "reportlab" },
  zip_bundle:            { label: "ZIP bundle (all)", ext: ".zip",  Icon: FileArchive, tone: "text-[#1E3A8A] bg-[#E0E7FF]", downloadable: true },
  manifest:              { label: "manifest.json",    ext: ".json", Icon: FileJson,    tone: "text-slate-700 bg-slate-100", downloadable: true },
  coverage:              { label: "coverage.json",    ext: ".json", Icon: FileJson,    tone: "text-slate-700 bg-slate-100", downloadable: true },
  validation:            { label: "validation.json",  ext: ".json", Icon: FileJson,    tone: "text-slate-700 bg-slate-100", downloadable: true },
  diagrams_placeholder:  { label: "Diagrams placeholder (Phase 6)", ext: ".md", Icon: FileText, tone: "text-slate-600 bg-slate-50", downloadable: true },
};

function formatBytes(n) {
  if (!Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function ExportCenter({ version, exportsInfo, manifest, exporting, onGenerate }) {
  const artifacts = exportsInfo?.artifacts || [];
  const stage = exportsInfo?.stage || manifest?.stage || "phase4-stage2";
  const has = artifacts.length > 0;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const downloadUrl = (fmt) =>
    `${backendUrl}/api/architecture-docs/versions/${version.id}/exports/${fmt}/download`;
  const manifestUrl = `${backendUrl}/api/architecture-docs/versions/${version.id}/exports/manifest`;
  const deps = manifest?.declared_dependencies;

  // Preferred rendering order: main downloadable formats first, then
  // metadata artifacts, then the phase-6 placeholder.
  const order = ["markdown", "html", "json", "docx", "pdf", "zip_bundle",
                 "manifest", "coverage", "validation", "diagrams_placeholder"];
  const sorted = [...artifacts].sort(
    (a, b) => order.indexOf(a.format) - order.indexOf(b.format),
  );

  return (
    <section
      data-testid="arch-docs-export-center"
      className="rounded-2xl border border-[#E5E5E0] bg-white p-5"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-[#1E3A8A] inline-flex items-center gap-2">
            <Package className="w-5 h-5" /> Export Center
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Downloadable, immutable artifacts for this documentation version.
            Every file ships with a SHA-256 hash and byte size — see
            <a href={manifestUrl} target="_blank" rel="noopener noreferrer"
               className="text-[#1E3A8A] hover:underline ml-1"
               data-testid="arch-docs-export-manifest-link">
              manifest.json
            </a>{" "}
            for the machine-readable inventory.{" "}
            <span className="uppercase tracking-wider text-[10px] ml-1 bg-[#E0E7FF] text-[#1E3A8A] px-1.5 py-0.5 rounded">
              {stage}
            </span>
          </p>
        </div>
        <button
          type="button"
          data-testid="arch-docs-generate-exports-btn"
          onClick={onGenerate}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#1E3A8A]/90 disabled:opacity-60"
        >
          {exporting
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <PlayCircle className="w-4 h-4" />}
          {has ? "Re-generate exports" : "Generate exports"}
        </button>
      </div>

      {deps?.direct?.length ? (
        <div
          className="text-[11px] text-slate-500 mb-3"
          data-testid="arch-docs-export-declared-deps"
        >
          <span className="font-medium text-slate-600">Declared runtime dependencies:</span>{" "}
          {deps.direct.map((p) => (
            <code key={p} className="mx-1 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{p}</code>
          ))}
          {deps.transitive?.length ? (
            <>
              <span className="ml-2 text-slate-400">+ transitive</span>{" "}
              {deps.transitive.map((p) => (
                <code key={p} className="mx-1 bg-slate-50 px-1.5 py-0.5 rounded font-mono text-slate-500">{p}</code>
              ))}
            </>
          ) : null}
        </div>
      ) : null}

      {!has ? (
        <div className="text-sm text-slate-500 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          No exports have been generated for this version yet. Click{" "}
          <span className="font-medium">Generate exports</span> to render all six formats
          (Markdown, HTML, JSON, Microsoft Word, PDF, and a ZIP bundle).
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full text-xs"
            data-testid="arch-docs-export-table"
          >
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-2 py-1.5">Format</th>
                <th className="text-left px-2 py-1.5">Filename</th>
                <th className="text-right px-2 py-1.5">Size</th>
                <th className="text-left px-2 py-1.5">SHA-256 (first 16)</th>
                <th className="text-right px-2 py-1.5">Download</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => {
                const meta = EXPORT_FORMAT_META[a.format] || {
                  label: a.format, Icon: FileText, tone: "text-slate-700 bg-slate-100",
                };
                const Icon = meta.Icon;
                return (
                  <tr
                    key={a.format}
                    data-testid={`arch-docs-export-row-${a.format}`}
                    className="border-t border-[#E5E5E0] hover:bg-slate-50/60"
                  >
                    <td className="px-2 py-1.5 align-top">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium ${meta.tone}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {meta.label}
                      </span>
                      {meta.badge && (
                        <div className="text-[10px] text-slate-400 mt-0.5 ml-1 font-mono">
                          via {meta.badge}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[11px] text-slate-700 align-top break-all">
                      {a.filename}
                    </td>
                    <td className="px-2 py-1.5 text-right text-slate-600 align-top whitespace-nowrap">
                      {formatBytes(a.size_bytes)}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500 align-top">
                      {a.sha256 ? `${a.sha256.slice(0, 16)}…` : "—"}
                    </td>
                    <td className="px-2 py-1.5 text-right align-top">
                      <a
                        data-testid={`arch-docs-export-download-${a.format}`}
                        href={downloadUrl(a.format)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1E3A8A] text-white text-[11px] font-medium hover:bg-[#1E3A8A]/90"
                      >
                        <Download className="w-3 h-3" /> Download
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Minimal markdown renderer supporting Phase 2 discovery output:
 *  `# h2`, `## h3`, `### h4` headings, GFM tables, bulleted lists,
 *  paragraphs, and inline `code`. Phase 3 replaces this with a full
 *  renderer that mirrors the backend HTML export exactly. */
function renderPlaceholderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  let key = 0;

  const renderInline = (txt) => {
    // Very small inline: `code` spans only, everything else raw text.
    const parts = txt.split(/(`[^`]+`)/g);
    return parts.map((p, idx) =>
      p.startsWith("`") && p.endsWith("`")
        ? <code key={idx} className="bg-slate-100 text-[13px] px-1 py-0.5 rounded font-mono text-slate-700">{p.slice(1, -1)}</code>
        : <span key={idx}>{p}</span>
    );
  };

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trimEnd();
    // Blank line
    if (!line.trim()) { i++; continue; }
    // Headings
    if (line.startsWith("### ")) { out.push(<h4 key={key++} className="text-sm font-semibold text-[#1E3A8A] mt-4 mb-2">{renderInline(line.slice(4))}</h4>); i++; continue; }
    if (line.startsWith("## "))  { out.push(<h3 key={key++} className="text-base font-semibold text-[#1E3A8A] mt-5 mb-2">{renderInline(line.slice(3))}</h3>); i++; continue; }
    if (line.startsWith("# "))   { out.push(<h2 key={key++} className="font-display text-2xl font-semibold text-[#1E3A8A] mt-2 mb-3">{renderInline(line.slice(2))}</h2>); i++; continue; }
    // Table
    if (line.startsWith("| ") && lines[i + 1] && /^\|[-\s|]+\|$/.test(lines[i + 1].trim())) {
      const headerCells = line.split("|").slice(1, -1).map((s) => s.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("| ")) {
        rows.push(lines[i].split("|").slice(1, -1).map((s) => s.trim()));
        i++;
      }
      out.push(
        <div key={key++} className="overflow-x-auto my-3">
          <table className="w-full text-xs border border-[#E5E5E0] rounded">
            <thead className="bg-slate-50">
              <tr>{headerCells.map((c, idx) => <th key={idx} className="text-left px-2 py-1.5 font-semibold text-[#1E3A8A]">{renderInline(c)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-t border-[#E5E5E0]">
                  {r.map((c, ci) => <td key={ci} className="px-2 py-1 text-slate-700 align-top">{renderInline(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    // Bullet list
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      out.push(<ul key={key++} className="list-disc pl-5 space-y-1 my-2">{items.map((it, ii) => <li key={ii} className="text-sm text-slate-700">{renderInline(it)}</li>)}</ul>);
      continue;
    }
    // Italic single-line "_..._"
    if (/^_.+_$/.test(line)) {
      out.push(<p key={key++} className="text-xs italic text-slate-500 my-2">{line.slice(1, -1)}</p>);
      i++; continue;
    }
    // Paragraph
    out.push(<p key={key++} className="text-sm text-slate-700 leading-6 my-2">{renderInline(line)}</p>);
    i++;
  }
  return out;
}

export default function ArchitectureDocs() {
  const [catalog, setCatalog] = useState(null);
  const [providers, setProviders] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null); // full doc { version, sections }
  const [coverage, setCoverage] = useState(null); // { version_id, report, generated_at }
  const [summary, setSummary] = useState(null); // { executive_summary, engineering_summary, warnings }
  const [selectedSectionKey, setSelectedSectionKey] = useState(null);
  const [useRenderedHtml, setUseRenderedHtml] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [activeJobId, setActiveJobId] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genNotes, setGenNotes] = useState("");
  // Phase 4 — Export Engine
  const [exports, setExports] = useState(null);        // { supported_formats, artifacts, stage }
  const [manifest, setManifest] = useState(null);      // full manifest.json body
  const [exporting, setExporting] = useState(false);
  const [exportJobId, setExportJobId] = useState(null);
  // Phase 5 — Version Center
  const [dashboard, setDashboard] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [compareDefaultB, setCompareDefaultB] = useState(null);

  // Ref mirror of `selectedVersion` so `loadVersions` can read the current
  // selection without taking `selectedVersion` as a dependency (which
  // would re-fire the mount effect on every version change).
  const selectedVersionRef = useRef(null);
  useEffect(() => { selectedVersionRef.current = selectedVersion; }, [selectedVersion]);

  const loadCatalog = useCallback(async () => {
    try {
      const [{ data: cat }, { data: prov }] = await Promise.all([
        api.get("/architecture-docs/catalog"),
        api.get("/architecture-docs/providers"),
      ]);
      setCatalog(cat);
      setProviders(prov.providers || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/architecture-docs/jobs", { params: { limit: 20 } });
      setJobs(data.jobs || []);
    } catch (e) {
      /* silent — jobs panel tolerates gaps */
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const { data } = await api.get("/architecture-docs/audit", { params: { limit: 50 } });
      setAudit(data.audit || []);
    } catch (e) {
      /* silent */
    }
  }, []);

  const loadExports = useCallback(async (versionId) => {
    try {
      const { data } = await api.get(`/architecture-docs/versions/${versionId}/exports`);
      setExports(data);
      try {
        const { data: m } = await api.get(`/architecture-docs/versions/${versionId}/exports/manifest`);
        setManifest(m);
      } catch (_) {
        setManifest(null);
      }
    } catch (_) {
      setExports(null);
      setManifest(null);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/architecture-docs/dashboard");
      setDashboard(data);
      setActiveVersionId(data.active_version_id || null);
    } catch (_) { /* silent */ }
  }, []);

  const loadTimeline = useCallback(async () => {
    try {
      const { data } = await api.get("/architecture-docs/timeline", { params: { limit: 100 } });
      setTimeline(data.timeline || []);
    } catch (_) { /* silent */ }
  }, []);

  const loadReadiness = useCallback(async (versionId) => {
    if (!versionId) { setReadiness(null); return; }
    try {
      const { data } = await api.get(`/architecture-docs/versions/${versionId}/readiness`);
      setReadiness(data);
    } catch (_) {
      setReadiness(null);
    }
  }, []);

  const openVersion = useCallback(async (versionId) => {
    try {
      const { data } = await api.get(`/architecture-docs/versions/${versionId}`);
      setSelectedVersion(data);
      setSelectedSectionKey(data.sections?.[0]?.key || null);
      // Coverage report is only present for versions generated via
      // Phase 2 discovery. Silently ignore 404 on scaffold-only versions.
      try {
        const { data: cov } = await api.get(`/architecture-docs/versions/${versionId}/coverage`);
        setCoverage(cov);
      } catch (_) {
        setCoverage(null);
      }
      // Phase 3 canonical summary + warnings.
      try {
        const { data: sm } = await api.get(`/architecture-docs/versions/${versionId}/summary`);
        setSummary(sm);
      } catch (_) {
        setSummary(null);
      }
      // Phase 4 — Export Engine artifacts + manifest.
      await loadExports(versionId);
      // Phase 5 — Readiness for the selected version.
      await loadReadiness(versionId);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [loadExports, loadReadiness]);

  const loadVersions = useCallback(async () => {
    try {
      const { data } = await api.get("/architecture-docs/versions", { params: { limit: 50 } });
      setVersions(data.versions || []);
      // Auto-select newest whenever selection is stale.
      if ((data.versions || []).length && !selectedVersionRef.current) {
        await openVersion((data.versions || [])[0].id);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [openVersion]);

  const handleRestore = async (versionId) => {
    try {
      await api.post(`/architecture-docs/versions/${versionId}/restore`);
      toast.success("Version restored as active reference.");
      await loadDashboard();
      await loadTimeline();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleCompareFromTimeline = (versionId) => {
    setCompareDefaultB(versionId);
    // Scroll to compare panel.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="arch-docs-compare-panel"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadCatalog(), loadVersions(), loadJobs(), loadAudit(),
                          loadDashboard(), loadTimeline()]);
      setLoading(false);
    })();
  }, [loadCatalog, loadVersions, loadJobs, loadAudit, loadDashboard, loadTimeline]);

  // Poll job progress + refresh version list while a job is active.
  useEffect(() => {
    if (!activeJobId) return undefined;
    const iv = setInterval(async () => {
      try {
        const { data: job } = await api.get(`/architecture-docs/jobs/${activeJobId}`);
        setJobs((prev) => [job, ...prev.filter((j) => j.id !== job.id)]);
        if (["succeeded", "failed", "cancelled"].includes(job.status)) {
          setActiveJobId(null);
          setGenerating(false);
          await loadVersions();
          await loadJobs();
          await loadAudit();
          if (job.status === "succeeded") toast.success("Documentation scaffold generated.");
          else if (job.status === "failed") toast.error(`Job failed: ${job.error || "unknown error"}`);
        }
      } catch (e) {
        /* silent poll error */
      }
    }, 1500);
    return () => clearInterval(iv);
  }, [activeJobId, loadVersions, loadJobs, loadAudit]);

  // Poll export job progress.
  useEffect(() => {
    if (!exportJobId) return undefined;
    const iv = setInterval(async () => {
      try {
        const { data: job } = await api.get(`/architecture-docs/jobs/${exportJobId}`);
        setJobs((prev) => [job, ...prev.filter((j) => j.id !== job.id)]);
        if (["succeeded", "failed", "cancelled"].includes(job.status)) {
          setExportJobId(null);
          setExporting(false);
          await loadJobs();
          await loadAudit();
          if (job.status === "succeeded") {
            toast.success("Export bundle ready — refreshed downloads below.");
            if (selectedVersion?.version?.id) await loadExports(selectedVersion.version.id);
          } else if (job.status === "failed") {
            toast.error(`Export failed: ${job.error || "unknown error"}`);
          }
        }
      } catch (e) {
        /* silent poll error */
      }
    }, 1500);
    return () => clearInterval(iv);
  }, [exportJobId, loadJobs, loadAudit, loadExports, selectedVersion]);

  const startExports = async () => {
    if (!selectedVersion?.version?.id) return;
    setExporting(true);
    try {
      const { data: job } = await api.post(
        `/architecture-docs/versions/${selectedVersion.version.id}/exports`
      );
      setExportJobId(job.id);
      setJobs((prev) => [job, ...prev]);
      toast.info("Export job started — polling for progress …");
    } catch (e) {
      setExporting(false);
      toast.error(formatApiError(e));
    }
  };

  const startGenerate = async () => {
    setGenerating(true);
    try {
      const { data: job } = await api.post("/architecture-docs/generate", {
        notes: genNotes || null,
      });
      setActiveJobId(job.id);
      setJobs((prev) => [job, ...prev]);
      setGenNotes("");
      toast.info("Scaffold generation started — poll for progress below.");
    } catch (e) {
      setGenerating(false);
      toast.error(formatApiError(e));
    }
  };

  const cancelJob = async (jobId) => {
    try {
      await api.post(`/architecture-docs/jobs/${jobId}/cancel`);
      toast.info("Cancellation requested.");
      await loadJobs();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const currentSection = useMemo(() => {
    if (!selectedVersion || !selectedSectionKey) return null;
    return (selectedVersion.sections || []).find((s) => s.key === selectedSectionKey) || null;
  }, [selectedVersion, selectedSectionKey]);

  return (
    <div className="space-y-6" data-testid="architecture-docs-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-[#1E3A8A]/10 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-[#1E3A8A]" />
                </div>
                <div>
                  <h1 className="font-display text-3xl font-semibold text-[#1E3A8A]">
                    Architecture &amp; Developer Handoff
                  </h1>
                  {providers.length > 0 && (
                    <div className="text-xs text-slate-500 mt-1" data-testid="arch-docs-providers-count">
                      {providers.length} discovery provider{providers.length === 1 ? "" : "s"} registered:{" "}
                      {providers.map((p) => p.label).join(" · ")}
                    </div>
                  )}
                </div>
              </div>
          <p className="text-sm text-slate-500 mt-2 max-w-3xl">
            The permanent Enterprise Technical Architecture, Knowledge Base &amp;
            Developer Handoff System for HarborWell Connect. Phase 1 delivers the
            module framework and empty documentation sections. Live discovery,
            generation, and export land in subsequent phases.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="arch-docs-refresh"
            onClick={() => { loadVersions(); loadJobs(); loadAudit(); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border border-[#E5E5E0] bg-white hover:bg-slate-50"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500" data-testid="arch-docs-loading">
          <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
          Loading module …
        </div>
      ) : (
        <>
          {/* Generator strip */}
          <section
            data-testid="arch-docs-generator"
            className="rounded-2xl border border-[#E5E5E0] bg-white p-4 sm:p-5"
          >
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[#1E3A8A]">Documentation Generator</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Runs a read-only background discovery pass: enumerates FastAPI
                  routes, backend modules, MongoDB collections + indexes, and
                  RBAC constants, then updates the auto-populated sections and
                  the coverage report. Never modifies existing collections.
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                data-testid="arch-docs-generator-notes"
                type="text"
                placeholder="Optional notes for this version (visible in Version History)…"
                value={genNotes}
                onChange={(e) => setGenNotes(e.target.value)}
                className="md:col-span-3 px-3 py-2 rounded-full border border-[#E5E5E0] bg-white text-sm"
              />
              <button
                type="button"
                data-testid="arch-docs-generate-btn"
                onClick={startGenerate}
                disabled={generating}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#1E3A8A]/90 disabled:opacity-60"
              >
                {generating
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <PlayCircle className="w-4 h-4" />}
                Generate documentation
              </button>
            </div>
          </section>

          {/* Two-column module content */}
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
            {/* Left rail — section catalog */}
            <aside
              data-testid="arch-docs-nav"
              className="rounded-2xl border border-[#E5E5E0] bg-white p-3"
            >
              <div className="text-xs uppercase tracking-wider text-slate-500 px-2 pt-1 pb-2">
                Sections
              </div>
              <nav className="space-y-1">
                {(catalog?.sections || []).map((s) => {
                  const active = selectedSectionKey === s.key;
                  return (
                    <button
                      key={s.key}
                      data-testid={`arch-docs-section-${s.key}`}
                      onClick={() => setSelectedSectionKey(s.key)}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-sm flex items-center justify-between ${active ? "bg-[#1E3A8A] text-white" : "hover:bg-slate-50 text-slate-700"}`}
                    >
                      <span className="truncate">{s.title}</span>
                      <ChevronRight className={`w-4 h-4 ${active ? "text-white" : "text-slate-400"}`} />
                    </button>
                  );
                })}
              </nav>
              <div className="text-[11px] text-slate-500 px-2 pt-3">
                Section content populates in Phase {catalog?.phase_status ? "2 – 3" : "later"}.
              </div>
            </aside>

            {/* Right column — section + version + jobs + audit */}
            <div className="space-y-5">
              {/* Selected section */}
              <section
                data-testid="arch-docs-section-body"
                className="rounded-2xl border border-[#E5E5E0] bg-white p-5 min-h-[220px]"
              >
                {!selectedVersion ? (
                  <div className="text-sm text-slate-500 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    No documentation version yet. Use the generator above to
                    create the first scaffold.
                  </div>
                ) : currentSection ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-slate-500">
                          Version {selectedVersion.version?.version_number} · Build {selectedVersion.version?.build_number}
                        </div>
                        <h2 className="font-display text-2xl font-semibold text-[#1E3A8A]">
                          {currentSection.title}
                        </h2>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="text-xs text-slate-500 flex items-center gap-1" data-testid="arch-docs-render-toggle-label">
                          <input
                            type="checkbox"
                            data-testid="arch-docs-render-toggle"
                            checked={useRenderedHtml}
                            onChange={(e) => setUseRenderedHtml(e.target.checked)}
                          />
                          Rendered HTML
                        </label>
                        <span className="text-xs text-slate-500">
                          Coverage {(currentSection.coverage_pct || 0).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    {useRenderedHtml && currentSection.body_html ? (
                      <article
                        className="prose prose-sm max-w-none"
                        data-testid="arch-docs-section-html"
                        // Backend HTML is stdlib-html-escaped end-to-end; no
                        // user-supplied content ever reaches this string.
                        dangerouslySetInnerHTML={{ __html: currentSection.body_html }}
                      />
                    ) : (
                      <article className="prose prose-sm max-w-none">
                        {renderPlaceholderMarkdown(currentSection.body_md)}
                      </article>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-slate-500">Select a section on the left.</div>
                )}
              </section>

              {/* Executive + Engineering summaries (Phase 3) */}
              {summary?.executive_summary && (
                <section
                  data-testid="arch-docs-summary-section"
                  className="rounded-2xl border border-[#E5E5E0] bg-white p-5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-[#1E3A8A]">Executive Summary</h3>
                    <div className="flex items-center gap-2">
                      <a
                        data-testid="arch-docs-download-md"
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/architecture-docs/versions/${selectedVersion.version.id}/rendered/markdown`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#1E3A8A] hover:underline"
                      >
                        Open .md
                      </a>
                      <a
                        data-testid="arch-docs-download-html"
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/architecture-docs/versions/${selectedVersion.version.id}/rendered/html`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#1E3A8A] hover:underline"
                      >
                        Open .html
                      </a>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 leading-6 mb-2"><strong>Platform purpose.</strong> {summary.executive_summary.platform_purpose}</p>
                  <p className="text-sm text-slate-700 leading-6 mb-2"><strong>Platform maturity.</strong> {summary.executive_summary.platform_maturity}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Primary capabilities</div>
                      <ul className="list-disc pl-5 space-y-0.5 text-sm text-slate-700">
                        {(summary.executive_summary.primary_capabilities || []).map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Recommended improvements</div>
                      <ul className="list-disc pl-5 space-y-0.5 text-sm text-slate-700">
                        {(summary.executive_summary.recommended_improvements || []).map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                  </div>
                  {summary.engineering_summary && (
                    <div className="mt-4">
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Engineering summary</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        {[
                          ["APIs",        summary.engineering_summary.total_apis],
                          ["Modules",     summary.engineering_summary.total_modules],
                          ["Collections", summary.engineering_summary.total_collections],
                          ["Permissions", summary.engineering_summary.total_permissions],
                          ["Background",  summary.engineering_summary.total_background_jobs],
                          ["Reports",     summary.engineering_summary.total_reports],
                          ["Integrations",summary.engineering_summary.total_integrations],
                          ["Coverage",    `${summary.engineering_summary.documentation_coverage_pct}%`],
                        ].map(([k, v]) => (
                          <div key={k} className="rounded-xl bg-slate-50 px-3 py-2" data-testid={`arch-docs-eng-${k.toLowerCase()}`}>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                            <div className="text-lg font-semibold text-[#1E3A8A]">{v}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* Engineering warnings (Phase 3) */}
              {summary?.warnings?.length > 0 && (
                <section
                  data-testid="arch-docs-warnings-section"
                  className="rounded-2xl border border-[#E5E5E0] bg-white p-5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-[#1E3A8A]">Engineering Warnings</h3>
                    <span className="text-xs text-slate-500">{summary.warnings.length} raised</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="arch-docs-warnings-table">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="text-left px-2 py-1">Severity</th>
                          <th className="text-left px-2 py-1">Code</th>
                          <th className="text-left px-2 py-1">Title</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.warnings.map((w, i) => (
                          <tr key={i} className="border-t border-[#E5E5E0]">
                            <td className={`px-2 py-1 font-medium uppercase text-[10px] ${
                              w.severity === "critical" ? "text-red-700" :
                              w.severity === "high"     ? "text-orange-700" :
                              w.severity === "medium"   ? "text-amber-700" :
                              w.severity === "low"      ? "text-slate-600" :
                                                          "text-slate-500"
                            }`}>{w.severity}</td>
                            <td className="px-2 py-1 font-mono text-[11px] text-slate-600">{w.code}</td>
                            <td className="px-2 py-1 text-slate-700">{w.title}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Phase 4 — Export Center */}
              {selectedVersion?.version?.id && (
                <ExportCenter
                  version={selectedVersion.version}
                  exportsInfo={exports}
                  manifest={manifest}
                  exporting={exporting}
                  onGenerate={startExports}
                  data-testid="arch-docs-export-center"
                />
              )}

              {/* Phase 5 — Governance & Version Center */}
              <GovernanceDashboard
                dashboard={dashboard}
                readiness={readiness}
                onRefresh={() => { loadDashboard(); loadTimeline(); }}
              />
              <DocumentationTimeline
                rows={timeline}
                activeVersionId={activeVersionId}
                onOpen={openVersion}
                onCompare={handleCompareFromTimeline}
                onRestore={handleRestore}
              />
              <CompareVersions
                versions={versions}
                defaultA={activeVersionId}
                defaultB={compareDefaultB}
              />
              <DocumentationSearch activeVersionId={activeVersionId} />
              {selectedVersion?.version?.id && (
                <IntegrityVerification versionId={selectedVersion.version.id} />
              )}

              {/* Coverage report (Phase 2) */}
              {coverage?.report && (
                <section
                  data-testid="arch-docs-coverage-section"
                  className="rounded-2xl border border-[#E5E5E0] bg-white p-5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-[#1E3A8A]">Coverage Report</h3>
                    <span className="text-xs text-slate-500" data-testid="arch-docs-coverage-total">
                      {coverage.report.total} items · {providers.length} providers
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {[
                      { label: "Automated",         key: "automated",         tone: "text-[#166534] bg-[#DCFCE7]" },
                      { label: "Manual",            key: "manual",            tone: "text-[#92400E] bg-[#FEF3C7]" },
                      { label: "Not documented",    key: "not_documented",    tone: "text-[#334155] bg-[#F1F5F9]" },
                      { label: "Unable to inspect", key: "unable_to_inspect", tone: "text-[#991B1B] bg-[#FEE2E2]" },
                    ].map((b) => (
                      <div
                        key={b.key}
                        data-testid={`arch-docs-coverage-${b.key}`}
                        className={`rounded-xl px-3 py-2 ${b.tone}`}
                      >
                        <div className="text-[11px] uppercase tracking-wider opacity-80">{b.label}</div>
                        <div className="text-xl font-semibold">{coverage.report[b.key] ?? 0}</div>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="text-left px-2 py-1">Kind</th>
                          <th className="text-right px-2 py-1">Total</th>
                          <th className="text-right px-2 py-1">Automated</th>
                          <th className="text-right px-2 py-1">Manual</th>
                          <th className="text-right px-2 py-1">Not doc.</th>
                          <th className="text-right px-2 py-1">Unable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(coverage.report.by_kind || {}).map(([kind, agg]) => (
                          <tr key={kind} className="border-t border-[#E5E5E0]">
                            <td className="px-2 py-1 font-medium text-slate-700">{kind}</td>
                            <td className="px-2 py-1 text-right text-slate-700">{agg.total}</td>
                            <td className="px-2 py-1 text-right text-[#166534]">{agg.automated}</td>
                            <td className="px-2 py-1 text-right text-[#92400E]">{agg.manual}</td>
                            <td className="px-2 py-1 text-right text-slate-500">{agg.not_documented}</td>
                            <td className="px-2 py-1 text-right text-[#991B1B]">{agg.unable_to_inspect}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {coverage.report.warnings?.length ? (
                    <div className="mt-3 text-xs text-amber-700">
                      <div className="font-medium mb-1">Provider warnings ({coverage.report.warnings.length})</div>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {coverage.report.warnings.slice(0, 6).map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  ) : null}
                </section>
              )}

              {/* Versions */}
              <section
                data-testid="arch-docs-versions-section"
                className="rounded-2xl border border-[#E5E5E0] bg-white p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-[#1E3A8A] inline-flex items-center gap-2">
                    <History className="w-5 h-5" /> Documentation Versions
                  </h3>
                  <span className="text-xs text-slate-500">{versions.length} total</span>
                </div>
                {versions.length === 0 ? (
                  <div className="text-sm text-slate-500">No versions yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="arch-docs-versions-table">
                      <thead>
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Version</th>
                          <th className="text-left px-3 py-2 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Generated</th>
                          <th className="text-left px-3 py-2 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Build</th>
                          <th className="text-left px-3 py-2 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Coverage</th>
                          <th className="text-left px-3 py-2 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Status</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {versions.map((v) => (
                          <tr
                            key={v.id}
                            data-testid={`arch-docs-version-row-${v.id}`}
                            className="border-t border-[#E5E5E0]"
                          >
                            <td className="px-3 py-2 font-medium">{v.version_number}</td>
                            <td className="px-3 py-2 text-slate-600">{formatRelative(v.generated_at)}</td>
                            <td className="px-3 py-2 text-slate-600 font-mono text-xs">{v.build_number}</td>
                            <td className="px-3 py-2 text-slate-600">{(v.coverage_pct || 0).toFixed(0)}%</td>
                            <td className="px-3 py-2"><span className="text-xs text-slate-500 uppercase tracking-wider">{v.status}</span></td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                data-testid={`arch-docs-open-version-${v.id}`}
                                onClick={() => openVersion(v.id)}
                                className="text-[#1E3A8A] text-xs hover:underline"
                              >
                                Open
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Jobs */}
              <section
                data-testid="arch-docs-jobs-section"
                className="rounded-2xl border border-[#E5E5E0] bg-white p-5"
              >
                <h3 className="text-lg font-semibold text-[#1E3A8A] mb-3">Background Jobs</h3>
                {jobs.length === 0 ? (
                  <div className="text-sm text-slate-500">No jobs yet.</div>
                ) : (
                  <ul className="space-y-2" data-testid="arch-docs-jobs-list">
                    {jobs.slice(0, 6).map((j) => (
                      <li
                        key={j.id}
                        data-testid={`arch-docs-job-${j.id}`}
                        className="flex items-center justify-between text-sm border border-[#E5E5E0] rounded-xl px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-slate-700">
                            <StatusPill status={j.status} />
                            <span className="font-mono text-[11px] text-slate-500 truncate">{j.id.slice(0, 12)}</span>
                            <span className="text-xs text-slate-500 uppercase tracking-wider">{j.phase}</span>
                          </div>
                          {j.progress_note ? (
                            <div className="text-xs text-slate-500 mt-1">{j.progress_note}</div>
                          ) : null}
                          {j.error ? (
                            <div className="text-xs text-red-600 mt-1">Error: {j.error}</div>
                          ) : null}
                        </div>
                        <div className="text-xs text-slate-500 ml-3">
                          {j.progress_pct ? `${j.progress_pct.toFixed(0)}%` : ""}
                        </div>
                        {(j.status === "queued" || j.status === "running") && (
                          <button
                            type="button"
                            data-testid={`arch-docs-cancel-job-${j.id}`}
                            onClick={() => cancelJob(j.id)}
                            className="ml-3 text-xs text-red-700 hover:underline"
                          >
                            Cancel
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Audit */}
              <section
                data-testid="arch-docs-audit-section"
                className="rounded-2xl border border-[#E5E5E0] bg-white p-5"
              >
                <h3 className="text-lg font-semibold text-[#1E3A8A] mb-3">Audit Trail</h3>
                {audit.length === 0 ? (
                  <div className="text-sm text-slate-500">No audit events yet.</div>
                ) : (
                  <ul className="space-y-1" data-testid="arch-docs-audit-list">
                    {audit.slice(0, 15).map((a) => (
                      <li
                        key={a.id}
                        className="text-xs text-slate-600 border-t border-[#E5E5E0] py-1.5 first:border-t-0 flex items-center gap-3"
                      >
                        <span className="text-slate-400 w-40 shrink-0">{formatRelative(a.ts)}</span>
                        <span className="font-medium uppercase tracking-wider w-20 shrink-0">{a.action}</span>
                        <span className="text-slate-500 w-24 shrink-0">{a.subject_type}</span>
                        <span className="font-mono text-[11px] text-slate-500 truncate">{a.subject_id}</span>
                        {a.actor_email && <span className="text-slate-500">· {a.actor_email}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
